import { useCallback, useEffect, useState } from "react";

/*
 * Taken from
 * https://css-tricks.com/block-links-the-search-for-a-perfect-solution/#method-4-sprinkle-javascript-on-the-second-method
 */
function useElement(): [
  (element: HTMLElement) => void,
  HTMLElement | undefined
] {
  const [element, setElement] = useState<HTMLElement>();

  const elementRef = useCallback((callbackElement: HTMLElement) => {
    if (callbackElement !== null) {
      setElement(callbackElement);
    }
  }, []);

  return [elementRef, element];
}

type InteractiveHTMLElement = HTMLAnchorElement | HTMLButtonElement | null;

function useInteractiveElements(): [
  interactiveRef: (element: InteractiveHTMLElement) => void,
  interactiveElements: InteractiveHTMLElement[]
] {
  const [interactiveElements, setInteractiveElements] = useState(
    [] as InteractiveHTMLElement[]
  );

  const interactiveRef = useCallback((newElement: InteractiveHTMLElement) => {
    if (newElement !== null) {
      setInteractiveElements((prevElements) => {
        const filteredElements = prevElements.filter(
          (element) => element !== newElement
        );
        return [...filteredElements, newElement];
      });
    }
  }, []);

  return [interactiveRef, interactiveElements];
}

function useEventListener(
  clickableElement: HTMLElement | undefined,
  handleClick: (event: MouseEvent) => void
): void {
  useEffect(() => {
    clickableElement?.addEventListener("click", handleClick);
    return () => {
      clickableElement?.removeEventListener("click", handleClick);
    };
  }, [clickableElement, handleClick]);
}

function useClickableBlock(): [
  (element: HTMLElement) => void,
  (element: HTMLAnchorElement) => void,
  (element: InteractiveHTMLElement) => void
] {
  const [blockRef, blockElement] = useElement();
  const [linkRef, linkElement] = useElement();
  const [interactiveRef, interactiveElements] = useInteractiveElements();

  const handleBlockClick = useCallback(
    (event: MouseEvent) => {
      const isLinkClicked = event.target === linkElement;
      const isInteractiveElementClicked = interactiveElements.some(
        (element) => element === event.target
      );
      const isTextSelected = window.getSelection()?.toString();
      if (!isLinkClicked && !isInteractiveElementClicked && !isTextSelected) {
        linkElement?.click();
      }
    },
    [linkElement, interactiveElements]
  );

  useEventListener(blockElement, handleBlockClick);

  return [blockRef, linkRef, interactiveRef];
}

export default useClickableBlock;
